import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ApiClient, type ExpertType } from "../core/api-client.js";
import { LeadMagnetWidgetBase } from "../core/widget-base.js";
import { widgetStyles } from "../core/widget-styles.js";
import {
  simulationFormSchema,
  simulationStepSchemas,
} from "../lead-magnets/simulation/form-schema.js";
import { mapSimulationFormToSubmitPayload } from "../lead-magnets/simulation/request-mapper.js";
import {
  getSimulationFormProgress,
  getInitialSimulationFormStep,
  getNextSimulationFormStep,
  getPreviousSimulationFormStep,
  type SimulationFormModel,
  type SimulationFormStep,
  type SimulationResultModel,
  type SimulationWidgetViewState,
} from "../lead-magnets/simulation/ui-state.js";

@customElement("lead-magnet-simulation-widget")
export class LeadMagnetSimulationWidget extends LeadMagnetWidgetBase {
  @property({ type: String })
  public override slug = "simulation";

  @property({ type: String, attribute: "api-base-url" })
  public apiBaseUrl = "";

  @property({ type: String })
  public override source = "landing_simulation_widget";

  @state()
  private viewState: SimulationWidgetViewState = "idle";

  @state()
  private step: SimulationFormStep = getInitialSimulationFormStep();

  @state()
  private form: SimulationFormModel = {
    slugParameter: "non-resident",
    email: "",
    firstName: "",
    source: "landing_simulation_widget",
    consent: false,
    prixAchat: "",
    loyerAnnuel: "",
    apport: "",
    dureePret: "",
    taux: "",
    expertComptable: false,
    courtier: false,
    chasseurImmobilier: false,
    honeypot: "",
  };

  @state()
  private resultData: SimulationResultModel | null = null;

  @state()
  private expertMessage = "";

  public static override styles = widgetStyles;

  protected override render() {
    const integrationError = this.getIntegrationError();
    if (integrationError) {
      return html`<p class="error">${integrationError}</p>`;
    }

    const progress = getSimulationFormProgress(this.step);
    const isIntroStep = this.step === "intro";
    const isContactStep = this.step === "contact";
    const isResultStep = this.step === "result";
    const isExpertStep = this.step === "expert-optin";
    const hasPrevious = ["statut", "bien", "financement", "contact"].includes(
      this.step,
    );

    return html`
      <form @submit=${this.onSubmit}>
        ${progress.current > 0
          ? html`<p class="step">
              Étape ${progress.current}/${progress.total}
            </p>`
          : null}
        ${this.renderCurrentStep()}

        <label style="display:none">
          Champ anti-bot
          <input
            name="honeypot"
            type="text"
            tabindex="-1"
            autocomplete="off"
            .value=${this.form.honeypot}
            @input=${(e: Event) => {
              this.form = {
                ...this.form,
                honeypot: (e.target as HTMLInputElement).value,
              };
            }}
          />
        </label>

        ${this.errorMessage
          ? html`<p class="error">${this.errorMessage}</p>`
          : null}
        ${isExpertStep
          ? null
          : html`
              <div class="actions">
                ${hasPrevious
                  ? html`
                      <button
                        type="button"
                        class="secondary"
                        ?disabled=${this.isLoading}
                        @click=${this.onPreviousStep}
                      >
                        Précédent
                      </button>
                    `
                  : null}
                ${isContactStep
                  ? html`<button ?disabled=${this.isLoading}>
                      ${this.isLoading
                        ? "Envoi en cours..."
                        : "Recevoir ma simulation gratuite"}
                    </button>`
                  : isResultStep
                    ? html`
                        <button
                          type="button"
                          class="secondary"
                          ?disabled=${this.isLoading}
                          @click=${this.resetFlow}
                        >
                          Recommencer
                        </button>
                      `
                    : html`
                        <button
                          type="button"
                          ?disabled=${this.isLoading}
                          @click=${this.onNextStep}
                        >
                          ${isIntroStep ? "Commencer" : "Suivant"}
                        </button>
                      `}
              </div>
            `}
      </form>
    `;
  }

  private renderCurrentStep() {
    switch (this.step) {
      case "intro":
        return this.renderIntroStep();
      case "statut":
        return this.renderStatutStep();
      case "bien":
        return this.renderBienStep();
      case "financement":
        return this.renderFinancementStep();
      case "contact":
        return this.renderContactStep();
      case "result":
        return this.renderResultStep();
      case "expert-optin":
        return this.renderExpertStep();
      default:
        return html``;
    }
  }

  private renderIntroStep() {
    return html`
      <div class="intro-badge">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        Gratuit - 2 min
      </div>

      <h2 class="intro-title">
        Estime la rentabilité de ton investissement locatif
      </h2>
      <p class="intro-copy">
        Simule ton projet en quelques clics et découvre si ton investissement
        est rentable. Rendement, cashflow et TRI instantanés.
      </p>

      <div class="intro-benefits">
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Rendement brut et net annuel</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Cashflow mensuel moyen sur 10 ans</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Taux de rendement interne (TRI)</span>
        </div>
      </div>

      <div class="intro-footer">
        <span>🔒 Tes données restent confidentielles</span>
      </div>
    `;
  }

  private renderStatutStep() {
    return html`
      <p class="intro-copy">
        Choisis ton statut fiscal pour adapter la simulation.
      </p>
      <label>
        Statut fiscal
        <select
          name="slugParameter"
          .value=${this.form.slugParameter}
          @change=${(e: Event) => {
            const select = e.target as HTMLSelectElement;
            this.form = {
              ...this.form,
              slugParameter: select.value as "resident" | "non-resident",
            };
          }}
        >
          <option value="non-resident">Non résident</option>
          <option value="resident">Résident</option>
        </select>
      </label>
    `;
  }

  private renderBienStep() {
    return html`
      <label>
        Prix d'achat du bien
        <input
          name="prixAchat"
          type="text"
          inputmode="numeric"
          placeholder="250 000"
          .value=${this.formatThousands(this.form.prixAchat)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "prixAchat",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>
      <label>
        Loyer annuel
        <input
          name="loyerAnnuel"
          type="text"
          inputmode="numeric"
          placeholder="14 400"
          .value=${this.formatThousands(this.form.loyerAnnuel)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "loyerAnnuel",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>
      <p class="helper">Renseigne le loyer annuel brut (charges comprises).</p>
    `;
  }

  private renderFinancementStep() {
    return html`
      <label>
        Apport personnel
        <input
          name="apport"
          type="text"
          inputmode="numeric"
          placeholder="30 000"
          .value=${this.formatThousands(this.form.apport)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "apport",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>
      <label>
        Durée du prêt (années)
        <input
          name="dureePret"
          type="text"
          inputmode="numeric"
          placeholder="20"
          .value=${this.form.dureePret}
          @input=${(e: Event) =>
            this.updateNumberField(
              "dureePret",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>
      <label>
        Taux du prêt (%)
        <input
          name="taux"
          type="text"
          inputmode="decimal"
          placeholder="3.5"
          .value=${this.form.taux}
          @input=${(e: Event) => {
            const input = e.target as HTMLInputElement;
            const pos = input.selectionStart;
            const val = input.value.replace(",", ".");
            this.form = { ...this.form, taux: val };
            if (val !== input.value) {
              input.value = val;
              input.setSelectionRange(pos, pos);
            }
          }}
        />
      </label>
    `;
  }

  private renderContactStep() {
    const prixAchat = this.parseNumber(this.form.prixAchat);
    const loyerAnnuel = this.parseNumber(this.form.loyerAnnuel);
    const rendementBrutEstim =
      prixAchat > 0 ? (loyerAnnuel / prixAchat) * 100 : 0;

    return html`
      <div class="preview-card">
        <div class="preview-row">
          <span class="preview-label">Prix d'achat</span>
          <span class="preview-value">${this.formatCurrency(prixAchat)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Loyer annuel</span>
          <span class="preview-value">${this.formatCurrency(loyerAnnuel)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Rendement brut</span>
          <span class="preview-value"
            >${rendementBrutEstim > 0
              ? `~${rendementBrutEstim.toFixed(1)}%`
              : "—"}</span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Rendement net</span>
          <span class="preview-value blurred"
            >█.██ %<span class="lock">🔒</span></span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Cashflow mensuel</span>
          <span class="preview-value blurred"
            >███ €<span class="lock">🔒</span></span
          >
        </div>
      </div>

      <p class="unlock-title">Débloquer ta simulation complète</p>

      <label>
        Email
        <input
          name="email"
          type="email"
          placeholder="ton@email.com"
          .value=${this.form.email}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              email: (e.target as HTMLInputElement).value,
            };
          }}
          required
        />
      </label>

      <label>
        Prénom
        <input
          name="firstName"
          type="text"
          placeholder="Ton prénom"
          .value=${this.form.firstName}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              firstName: (e.target as HTMLInputElement).value,
            };
          }}
        />
      </label>

      <label style="display: flex; align-items: center; gap: 8px;">
        <input
          name="consent"
          type="checkbox"
          .checked=${this.form.consent}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              consent: (e.target as HTMLInputElement).checked,
            };
          }}
          required
        />
        <span>J'accepte de recevoir le résultat par e-mail.</span>
      </label>

      <div class="reassurance">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
        <span>Données sécurisées - Aucun spam, désabonnement en 1 clic</span>
      </div>
    `;
  }

  private renderResultStep() {
    if (!this.resultData) {
      return html`
        <div class="result-card">
          <p class="result-title">Résultat indisponible</p>
          <p class="helper">
            Impossible d'afficher le résultat pour le moment.
          </p>
        </div>
      `;
    }

    const cashflowClass =
      this.resultData.moyenneCashFlow >= 0 ? "positive" : "negative";

    return html`
      <div class="result-card">
        <p class="result-title">Rentabilité de ton investissement</p>
        <div class="preview-row">
          <span class="preview-label">Rendement brut</span>
          <span class="preview-value"
            >${this.resultData.rendementBrut.toFixed(2)} %</span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Rendement net</span>
          <span class="preview-value"
            >${this.resultData.rendementNet.toFixed(2)} %</span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Cashflow mensuel moyen (sur 10 ans)</span>
          <span class="preview-value ${cashflowClass}"
            >${this.formatCurrency(this.resultData.moyenneCashFlow)}</span
          >
        </div>
        <p class="helper">
          Le cashflow affiché ici correspond à une moyenne mensuelle calculée
          sur 10 ans.
        </p>
        ${this.resultData.triDixAns > 0
          ? html`
              <div class="preview-row">
                <span class="preview-label">TRI à 10 ans</span>
                <span class="preview-value"
                  >${this.resultData.triDixAns.toFixed(2)} %</span
                >
              </div>
            `
          : null}
        <div
          class="preview-row"
          style="border-top: 2px solid #1674ee; padding-top: 8px;"
        >
          <span class="preview-label" style="font-weight: 700; color: #0f172a;"
            >TRI max (année ${this.resultData.triAnnee})</span
          >
          <span class="preview-value" style="font-size: 16px; color: #1674ee;"
            >${this.resultData.triMax.toFixed(2)} %</span
          >
        </div>
        <p class="helper">
          Repère indicatif : autour de 3 %, tu restes proche d'un placement
          défensif type Livret A. Entre 7 % et 10 %, le projet est généralement
          considéré comme intéressant. Au-delà, le rendement est plus dynamique.
        </p>
      </div>
      ${this.renderExpertSection()}
    `;
  }

  private renderExpertStep() {
    return this.renderExpertSection();
  }

  private renderExpertSection() {
    return html`
      <div class="expert-card" style="margin-top: 0.75rem;">
        <span class="expert-eyebrow">Accompagnement</span>
        <p class="expert-title">
          Tu veux avancer avec des experts de confiance ?
        </p>
        <p class="expert-copy">
          Sélectionne les partenaires que tu veux solliciter selon ton projet.
        </p>

        <div class="expert-options">
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.expertComptable}
              @change=${() => {
                this.form = {
                  ...this.form,
                  expertComptable: !this.form.expertComptable,
                };
              }}
            />
            <span>Expert-comptable</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.courtier}
              @change=${() => {
                this.form = { ...this.form, courtier: !this.form.courtier };
              }}
            />
            <span>Courtier</span>
          </label>
          <label class="expert-option">
            <input
              type="checkbox"
              .checked=${this.form.chasseurImmobilier}
              @change=${() => {
                this.form = {
                  ...this.form,
                  chasseurImmobilier: !this.form.chasseurImmobilier,
                };
              }}
            />
            <span>Chasseur immobilier</span>
          </label>
        </div>

        <button
          type="button"
          ?disabled=${this.isLoading}
          @click=${this.submitExpertOptin}
        >
          ${this.isLoading
            ? "Envoi en cours..."
            : "Être mis en relation avec ces experts"}
        </button>

        ${this.expertMessage
          ? html`<p class="expert-feedback ${this.expertMessage.startsWith("Sélectionne") ? "error" : ""}">${this.expertMessage}</p>`
          : null}
        ${this.expertMessage && !this.expertMessage.startsWith("Sélectionne")
          ? html`<button
              type="button"
              class="expert-reset"
              ?disabled=${this.isLoading}
              @click=${this.resetFlow}
            >
              Recommencer
            </button>`
          : null}
      </div>
    `;
  }

  private onNextStep = (): void => {
    if (!this.validateStep(this.step)) {
      this.viewState = "error";
      return;
    }
    this.trackStep(this.step);
    this.viewState = "idle";
    this.step = getNextSimulationFormStep(this.step);
  };

  private onPreviousStep = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getPreviousSimulationFormStep(this.step);
  };

  private onSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (this.step !== "contact") {
      this.onNextStep();
      return;
    }

    if (!this.validateStep("contact")) {
      this.viewState = "error";
      return;
    }

    const parsed = simulationFormSchema.safeParse(
      this.buildFinalValidationInput(),
    );
    if (!parsed.success) {
      this.viewState = "error";
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return;
    }

    this.startLoading();
    this.viewState = "submitting";

    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      const payload = mapSimulationFormToSubmitPayload({
        ...this.form,
        source: parsed.data.source,
        consent: parsed.data.consent,
      });

      const response = await apiClient.submitLeadMagnet(
        "simulation",
        payload,
        this.makeIdempotencyKey(),
      );
      this.resultData = this.mapApiResult(response.result.payload);
      this.step = "result";
      this.viewState = "idle";
      this.errorMessage = "";
      this.trackSubmit();
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Soumission impossible.";
      this.viewState = "error";
      this.setError(msg);
      this.trackError(this.step, msg);
    } finally {
      this.stopLoading();
    }
  };

  private validateStep(step: SimulationFormStep): boolean {
    if (step === "result" || step === "expert-optin") {
      this.errorMessage = "";
      return true;
    }
    const schema = simulationStepSchemas[step];
    const parsed = schema.safeParse(this.buildStepValidationInput(step));
    if (!parsed.success) {
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return false;
    }
    if (step === "contact") {
      this.form = { ...this.form, source: this.getSourceValue() };
    }
    this.errorMessage = "";
    return true;
  }

  private buildStepValidationInput(
    step: SimulationFormStep,
  ): Record<string, unknown> {
    switch (step) {
      case "bien":
        return {
          prixAchat: this.form.prixAchat,
          loyerAnnuel: this.form.loyerAnnuel,
        };
      case "financement":
        return {
          apport: this.form.apport,
          dureePret: this.form.dureePret,
          taux: this.form.taux,
        };
      case "contact":
        return {
          email: this.form.email,
          firstName: this.form.firstName,
          source: this.getSourceValue(),
          consent: this.form.consent,
          honeypot: this.form.honeypot,
        };
      default:
        return {};
    }
  }

  private buildFinalValidationInput(): Record<string, unknown> {
    return { ...this.form, source: this.getSourceValue() };
  }

  private submitExpertOptin = async (): Promise<void> => {
    const expertTypes = this.getExpertTypes();
    if (expertTypes.length === 0) {
      this.expertMessage = "Sélectionne au moins un expert pour être mis en relation.";
      return;
    }
    this.startLoading();
    this.errorMessage = "";
    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      await apiClient.submitExpertOptin("simulation", {
        email: this.form.email.trim(),
        firstName: this.form.firstName.trim() || undefined,
        source: this.getSourceValue(),
        expertTypes,
      });
      this.expertMessage =
        expertTypes.length > 0
          ? "Parfait, ta demande a bien été enregistrée."
          : "Merci, ta simulation est terminée.";
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : "Soumission impossible.";
    } finally {
      this.stopLoading();
    }
  };

  private resetFlow = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getInitialSimulationFormStep();
    this.resultData = null;
    this.expertMessage = "";
    this.form = {
      slugParameter: "non-resident",
      email: "",
      firstName: "",
      source: this.getSourceValue(),
      consent: false,
      prixAchat: "",
      loyerAnnuel: "",
      apport: "",
      dureePret: "",
      taux: "",
      expertComptable: false,
      courtier: false,
      chasseurImmobilier: false,
      honeypot: "",
    };
  };

  private getExpertTypes(): ExpertType[] {
    const expertTypes: ExpertType[] = [];
    if (this.form.expertComptable) expertTypes.push("expert_comptable");
    if (this.form.courtier) expertTypes.push("courtier");
    if (this.form.chasseurImmobilier) {
      expertTypes.push("chasseur_immobilier");
    }
    return expertTypes;
  }

  private mapApiResult(
    payload: Record<string, unknown>,
  ): SimulationResultModel {
    const resume =
      typeof payload.resume === "object" && payload.resume !== null
        ? (payload.resume as Record<string, unknown>)
        : payload;

    return {
      rendementBrut: toNumber(resume.rendement_brut) ?? 0,
      rendementNet: toNumber(resume.rendement_net) ?? 0,
      moyenneCashFlow:
        toNumber(resume.moyenne_cashFlow) ??
        toNumber(resume.moyenne_cashflow) ??
        0,
      triMax: toNumber(resume.tri_max) ?? 0,
      triAnnee: toNumber(resume.tri_annee) ?? 0,
      triDixAns: toNumber(resume.tri_dix_ans) ?? 0,
    };
  }

  private makeIdempotencyKey(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `sim_${crypto.randomUUID()}`;
    }
    return `sim_${Date.now()}`;
  }

  private getSourceValue(): string {
    return this.source.trim() || this.form.source;
  }

  private updateNumberField(
    field: "prixAchat" | "loyerAnnuel" | "apport" | "dureePret",
    value: string,
  ): void {
    this.form = { ...this.form, [field]: value.replaceAll(/\D+/g, "") };
  }

  private parseNumber(value: string): number {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  }

  private formatThousands(value: string): string {
    const digits = value.replaceAll(/\D+/g, "").replace(/^0+(?=\d)/, "");
    if (!digits) return "";
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  private getIntegrationError(): string | null {
    if (this.slug !== "simulation")
      return 'Ce widget supporte uniquement le slug "simulation".';
    const baseUrl = this.apiBaseUrl.trim();
    if (!baseUrl)
      return "Configuration invalide: l'attribut `api-base-url` est obligatoire.";
    try {
      const parsed = new URL(baseUrl);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "Configuration invalide: `api-base-url` doit utiliser http ou https.";
      }
    } catch {
      return "Configuration invalide: `api-base-url` doit être une URL absolue.";
    }
    return null;
  }
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

declare global {
  interface HTMLElementTagNameMap {
    "lead-magnet-simulation-widget": LeadMagnetSimulationWidget;
  }
}
